// KaTeX auto-render functionality adapted for vueapp
// Copied and adapted from src/main/es6/edreports/cms/components/editor/katex/auto-render.mjs

declare global {
  interface Window {
    katex: any;
  }
}

interface MathData {
  type: 'text' | 'math';
  data: string;
  rawData?: string;
  display?: boolean;
}

interface AutoRenderOptions {
  delimiters?: Array<{
    left: string;
    right: string;
    display: boolean;
  }>;
  ignoredTags?: string[];
  ignoredClasses?: string[];
  errorCallback?: (msg: string, err: any) => void;
  preProcess?: (math: string) => string;
  macros?: Record<string, any>;
}

const findEndOfMath = function findEndOfMath(
  delimiter: string,
  text: string,
  startIndex: number,
): number {
  // Adapted from
  // https://github.com/Khan/perseus/blob/master/src/perseus-markdown.jsx
  let index = startIndex;
  let braceLevel = 0;
  const delimLength = delimiter.length;

  while (index < text.length) {
    const character = text[index];

    if (
      braceLevel <= 0 &&
      text.slice(index, index + delimLength) === delimiter
    ) {
      return index;
    } else if (character === '\\') {
      index++;
    } else if (character === '{') {
      braceLevel++;
    } else if (character === '}') {
      braceLevel--;
    }

    index++;
  }

  return -1;
};

const splitAtDelimiters = function splitAtDelimiters(
  startData: MathData[],
  leftDelim: string,
  rightDelim: string,
  display: boolean,
): MathData[] {
  const finalData: MathData[] = [];

  for (let i = 0; i < startData.length; i++) {
    if (startData[i].type === 'text') {
      const text = startData[i].data;
      let lookingForLeft = true;
      let currIndex = 0;
      let nextIndex;
      nextIndex = text.indexOf(leftDelim);

      if (nextIndex !== -1) {
        currIndex = nextIndex;
        finalData.push({
          type: 'text',
          data: text.slice(0, currIndex),
        });
        lookingForLeft = false;
      }

      while (true) {
        if (lookingForLeft) {
          nextIndex = text.indexOf(leftDelim, currIndex);

          if (nextIndex === -1) {
            break;
          }

          finalData.push({
            type: 'text',
            data: text.slice(currIndex, nextIndex),
          });
          currIndex = nextIndex;
        } else {
          nextIndex = findEndOfMath(
            rightDelim,
            text,
            currIndex + leftDelim.length,
          );

          if (nextIndex === -1) {
            break;
          }

          finalData.push({
            type: 'math',
            data: text.slice(currIndex + leftDelim.length, nextIndex),
            rawData: text.slice(currIndex, nextIndex + rightDelim.length),
            display: display,
          });
          currIndex = nextIndex + rightDelim.length;
        }

        lookingForLeft = !lookingForLeft;
      }

      finalData.push({
        type: 'text',
        data: text.slice(currIndex),
      });
    } else {
      finalData.push(startData[i]);
    }
  }

  return finalData;
};

const splitWithDelimiters = function splitWithDelimiters(
  text: string,
  delimiters: AutoRenderOptions['delimiters'],
): MathData[] {
  let data: MathData[] = [
    {
      type: 'text',
      data: text,
    },
  ];

  if (!delimiters) return data;

  for (let i = 0; i < delimiters.length; i++) {
    const delimiter = delimiters[i];
    data = splitAtDelimiters(
      data,
      delimiter.left,
      delimiter.right,
      delimiter.display || false,
    );
  }

  return data;
};

const renderMathInText = function renderMathInText(
  text: string,
  optionsCopy: any,
): DocumentFragment | null {
  const data = splitWithDelimiters(text, optionsCopy.delimiters);

  if (data.length === 1 && data[0].type === 'text') {
    // There is no formula in the text.
    // Let's return null which means there is no need to replace
    // the current text node with a new one.
    return null;
  }

  const fragment = document.createDocumentFragment();

  for (let i = 0; i < data.length; i++) {
    if (data[i].type === 'text') {
      fragment.appendChild(document.createTextNode(data[i].data));
    } else {
      const span = document.createElement('span');
      let math = data[i].data;

      // Override any display mode defined in the settings with that
      // defined by the text itself
      optionsCopy.displayMode = data[i].display;

      try {
        if (optionsCopy.preProcess) {
          math = optionsCopy.preProcess(math);
        }

        // Use global katex if available
        if (typeof window !== 'undefined' && window.katex) {
          window.katex.render(math, span, optionsCopy);
        } else {
          // Fallback: just display the raw math
          span.textContent = data[i].rawData || data[i].data;
        }
      } catch (e) {
        if (
          typeof window !== 'undefined' &&
          window.katex &&
          e instanceof window.katex.ParseError
        ) {
          optionsCopy.errorCallback(
            'KaTeX auto-render: Failed to parse `' + data[i].data + '` with ',
            e,
          );
        } else {
          optionsCopy.errorCallback('Math render error: ', e);
        }
        fragment.appendChild(
          document.createTextNode(data[i].rawData || data[i].data),
        );
        continue;
      }

      fragment.appendChild(span);
    }
  }

  return fragment;
};

const renderElem = function renderElem(elem: Element, optionsCopy: any): void {
  for (let i = 0; i < elem.childNodes.length; i++) {
    const childNode = elem.childNodes[i];

    if (childNode.nodeType === 3) {
      // Text node
      const frag = renderMathInText(childNode.textContent || '', optionsCopy);

      if (frag) {
        i += frag.childNodes.length - 1;
        elem.replaceChild(frag, childNode);
      }
    } else if (childNode.nodeType === 1) {
      // Element node
      const element = childNode as Element;
      const className = ' ' + element.className + ' ';
      const shouldRender =
        optionsCopy.ignoredTags.indexOf(element.nodeName.toLowerCase()) ===
          -1 &&
        optionsCopy.ignoredClasses.every(
          (x: string) => className.indexOf(' ' + x + ' ') === -1,
        );

      if (shouldRender) {
        renderElem(element, optionsCopy);
      }
    }
    // Otherwise, it's something else, and ignore it.
  }
};

export const renderMathInElement = function renderMathInElement(
  elem: Element,
  options: AutoRenderOptions = {},
): void {
  if (!elem) {
    throw new Error('No element provided to render');
  }

  const optionsCopy: any = {};

  // Copy options
  for (const option in options) {
    if (options.hasOwnProperty(option)) {
      (optionsCopy as any)[option] = (options as any)[option];
    }
  }

  // Default options
  optionsCopy.delimiters = optionsCopy.delimiters || [
    {
      left: '$$',
      right: '$$',
      display: true,
    },
    {
      left: '\\(',
      right: '\\)',
      display: false,
    },
    // LaTeX uses $…$, but it ruins the display of normal `$` in text:
    // {left: "$", right: "$", display: false},
    //  \[…\] must come last in this array. Otherwise, renderMathInElement
    //  will search for \[ before it searches for $$ or  \(
    // That makes it susceptible to finding a \\[0.3em] row delimiter and
    // treating it as if it were the start of a KaTeX math zone.
    {
      left: '\\[',
      right: '\\]',
      display: true,
    },
  ];

  optionsCopy.ignoredTags = optionsCopy.ignoredTags || [
    'script',
    'noscript',
    'style',
    'textarea',
    'pre',
    'code',
    'option',
  ];
  optionsCopy.ignoredClasses = optionsCopy.ignoredClasses || [];
  optionsCopy.errorCallback = optionsCopy.errorCallback || console.error;

  // Enable sharing of global macros defined via `\gdef` between different
  // math elements within a single call to `renderMathInElement`.
  optionsCopy.macros = optionsCopy.macros || {};

  renderElem(elem, optionsCopy);
};

export default renderMathInElement;
